import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SelectDropDownModule } from 'ngx-select-dropdown';
import { LanguageListModel } from '../../../../core/models/customer/language-list.model';
import { CustomersService } from '../../../../core/services/customer/customers.service';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { lettersOnlyValidator } from '../../../shared/validators/validators';
import { tap } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../core/services/toast.service';
@Component({
  selector: 'app-create-edit-branch',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectDropDownModule,
    RouterModule,
    TranslateModule,
  ],
  templateUrl: './create-edit-branch.component.html',
  styleUrl: './create-edit-branch.component.scss',
})
export class CreateEditBranchComponent {
  branchForm: FormGroup;
  roles: any = [];
  branchId: string = '';
  maxDate: string;
  currentUser: any = null;
  languageDropdownConfig: any = {
    displayKey: 'name',
    valueKey: 'code',
    search: true,
    height: '200px',
    placeholder: this.translate.instant('selectLanguage'),
    limitTo: 0,
    moreText: 'more',
  };
  customerDropdownConfig: any = {
    displayKey: 'code',
    valueKey: 'id',
    search: true,
    height: '200px',
    placeholder: this.translate.instant('selectCustomer'),
    limitTo: 0,
    moreText: 'more',
  };

  isOverlayLoading: boolean = false;
  languages: LanguageListModel[] = [];
  customerCodes: { id: string; code: string }[] = [];
  deleteModalId: string = '';

  constructor(
    private router: Router,
    private customersService: CustomersService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private toastService: ToastService
  ) {
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.branchForm = this.fb.group({
      name: ['', [Validators.required, lettersOnlyValidator()]],
      customerId: ['', [Validators.required]],
      languageCode: ['', [Validators.required]],
    });
    this.getLanguages();
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    this.loadInitialData();
  }

  private loadInitialData() {
    this.getCustomerCodes().subscribe({
      next: () => {
        this.getBranchById();
      },
    });
  }

  saveBranch(e: Event) {
    e.preventDefault();

    const prepareFormData = (formValue: any) => {
      const data = { ...formValue };
      if (data.customerId?.id) {
        data.customerId = data.customerId.id;
      }
      if (data.languageCode?.code) {
        data.languageCode = data.languageCode.code;
      }
      return data;
    };

    if (this.branchId) {
      if (this.branchForm.valid) {
        this.isOverlayLoading = true;

        const formData = prepareFormData(this.branchForm.value);
        formData.id = this.branchId;

        this.customersService
          .editCustomerBranch(this.branchId, formData)
          .subscribe({
            next: () => {
              this.toastService.showToast(
                this.translate.instant('branchEditedSuccessfully'),
                'success'
              );

              setTimeout(() => {
                this.router.navigate(['/branch/list']);
              }, 2000);
            },
          });
      } else {
        this.toastService.showToast(
          this.translate.instant(
            'allFieldsAreRequiredAndMustBeFilledWithValidValues'
          ),
          'warning'
        );
        this.branchForm.markAllAsTouched();
      }
    } else {
      if (this.branchForm.valid) {
        this.isOverlayLoading = true;

        const formData = prepareFormData(this.branchForm.value);

        this.customersService.createBranch(formData).subscribe({
          next: () => {
            this.toastService.showToast(
              this.translate.instant('branchCreatedSuccessfully'),
              'success'
            );

            setTimeout(() => {
              this.router.navigate(['/branch/list']);
            }, 2000);
          },
        });
      } else {
        this.toastService.showToast(
          this.translate.instant(
            'allFieldsAreRequiredAndMustBeFilledWithValidValues'
          ),
          'warning'
        );
        this.branchForm.markAllAsTouched();
      }
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.branchForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  private getBranchById() {
    this.branchId = this.route.snapshot.paramMap.get('id')!;
    if (this.branchId) {
      this.customersService.getCustomerBranch(this.branchId).subscribe({
        next: (res) => {
          this.branchForm.patchValue({
            name: res.name,
            customerId: this.getCustomerById(res.customerId),
            languageCode: this.getOrganizationLanguage(res.languageCode),
          });
        },
      });
    }
    this.branchForm.patchValue({
      applications: [],
    });
  }

  private getLanguages() {
    this.languages = this.customersService.allLanguages();
  }

  private getCustomerCodes() {
    return this.customersService.allCustomerCodes().pipe(
      tap((res) => {
        this.customerCodes = res;
      })
    );
  }

  private getOrganizationLanguage(languageCode: string): any {
    return this.languages.find((item) => item.code == languageCode);
  }

  private getCustomerById(customerId: string): any {
    return this.customerCodes.find((item) => item.id == customerId);
  }
}
